import { insert } from "@utdanningsdirektoratet/utils/array";
import { assert } from "@utdanningsdirektoratet/utils/assert";
import FroalaEditor, { FroalaOptions } from "froala-editor";
import baseConfig from "./baseConfig";
import baseDomPurifyConfig from "./baseDomPurifyConfig";
import { Config, ToolbarType } from "./types";

class ConfigBuilder {
  config: Config;

  constructor(initialConfig?: Config) {
    this.config = initialConfig ?? { ...baseConfig, ...baseDomPurifyConfig };
  }

  withAllowedAttr = (
    attrs: ((prev: Array<string>) => Array<string>) | string | string[],
  ): ConfigBuilder => {
    if (typeof attrs === "function") {
      this.config = {
        ...this.config,
        ALLOWED_ATTR: attrs(this.config.ALLOWED_ATTR ?? []),
      };
    } else {
      const attrArr = Array.isArray(attrs) ? attrs : [attrs];
      this.config = {
        ...this.config,
        ALLOWED_ATTR: [...(this.config.ALLOWED_ATTR ?? []), ...attrArr],
      };
    }

    return this;
  };

  withKeepContent = (keepContent: boolean): ConfigBuilder => {
    this.config = {
      ...this.config,
      KEEP_CONTENT: keepContent,
    };

    return this;
  };

  withReturnDom = (returnDom: boolean): ConfigBuilder => {
    this.config = {
      ...this.config,
      RETURN_DOM: returnDom,
    };

    return this;
  };

  withAllowedTags = (
    tags: ((prev: Array<string>) => Array<string>) | string | string[],
  ): ConfigBuilder => {
    if (typeof tags === "function") {
      this.config = {
        ...this.config,
        htmlAllowedTags: tags(this.config.htmlAllowedTags ?? []),
        ALLOWED_TAGS: tags(this.config.ALLOWED_TAGS ?? []),
      };
    } else {
      const tagsArr = Array.isArray(tags) ? tags : [tags];
      this.config = {
        ...this.config,
        htmlAllowedTags: [...(this.config.htmlAllowedTags ?? []), ...tagsArr],
        ALLOWED_TAGS: [...(this.config.ALLOWED_TAGS ?? []), ...tagsArr],
      };
    }

    return this;
  };

  withToolbar = (
    toolbar: (prev: ToolbarType) => ToolbarType,
  ): ConfigBuilder => {
    assert(
      typeof toolbar === "function",
      "withToolbar må ta inn en funksjon som tar imot nåværende config og returnerer den nye",
    );
    this.config = {
      ...this.config,
      toolbarButtons: toolbar(this.config.toolbarButtons ?? []),
    };

    return this;
  };

  withFontSize = (toolbarIndex = 9): ConfigBuilder => {
    this.withToolbar((prev) => insert(prev, toolbarIndex, "fontSize"));
    this.withPlugins((prev) => [...(prev ?? []), "fontSize"]);

    return this;
  };

  withPlugins = (
    plugins: (prev: Array<string>) => Array<string>,
  ): ConfigBuilder => {
    assert(
      typeof plugins === "function",
      "withPlugins må ta inn en funksjon som tar imot nåværende config og returnerer den nye",
    );
    this.config = {
      ...this.config,
      pluginsEnabled: plugins(this.config.pluginsEnabled ?? []),
    };

    return this;
  };

  withImages = (
    url?: string,
    params: FroalaOptions["imageUploadParams"] = {},
  ): ConfigBuilder => {
    if (typeof url === "string") {
      this.config = {
        ...this.config,
        imageUploadURL: url,
        imageUploadRemoteUrls: true,
        imageUploadParams: params,
        // Disable CORS proxy because we need to set one up instead of using froala's
        // This doesn't really disable it, but it prevents cors-anywhere.froala.com to be used
        imageCORSProxy: "",
      };
    }

    this.config = {
      ...this.config,
      pluginsEnabled: [...(this.config.pluginsEnabled ?? []), "image"],
      toolbarButtons: [
        ...(this.config.toolbarButtons ?? []),
        "|",
        "insertImage",
      ],
    };

    this.withAllowedTags("img");
    this.withAllowedAttr(["src", "alt", "width", "height"]);

    return this;
  };

  withTrackChanges = (
    options = { showChangesEnabled: true, trackChangesEnabled: true },
  ): ConfigBuilder => {
    this.config = {
      ...this.config,
      pluginsEnabled: [...(this.config.pluginsEnabled ?? []), "track_changes"],
      toolbarButtons: [
        ...(this.config.toolbarButtons ?? []),
        "|",
        "trackChanges",
      ],
      ...options,
    };

    return this;
  };

  withPlaceholders = (
    toolbarName: string,
    options: Record<string, string>,
    callback: (cmd: string, val: string) => void,
  ): ConfigBuilder => {
    this.withAllowedTags("input");
    this.withAllowedAttr(["disabled", "value", "type"]);

    this.withToolbar((toolbar) => [...toolbar, "|", toolbarName]);

    this.config = {
      ...this.config,
      onInit: [
        ...this.config.onInit,
        () => {
          FroalaEditor.DefineIcon(toolbarName, {
            NAME: toolbarName,
            template: "text",
          });
          FroalaEditor.RegisterCommand(toolbarName, {
            title: toolbarName,
            type: "dropdown",
            focus: false,
            undo: false,
            refreshAfterCallback: true,
            options,
            callback(_cmd, val) {
              callback(_cmd, val);
            },
          });
        },
      ],
      pasteDeniedAttrs: ["id", "style"],
    };

    return this;
  };

  withHighlights = (toolbarName = "Markér"): ConfigBuilder => {
    this.withPlugins((plugins) => [...plugins, "inlineClass"]);
    this.withToolbar((toolbar) => [...toolbar, "|", "kommentar"]);

    this.config = {
      ...this.config,
      onInit: [
        ...this.config.onInit,
        (editor) => {
          FroalaEditor.DefineIcon("kommentar", {
            NAME: toolbarName,
            template: "text",
          });
          FroalaEditor.RegisterCommand("kommentar", {
            title: toolbarName,
            focus: true,
            undo: true,
            plugin: "inlineClass",
            callback() {
              (this as unknown as typeof editor).inlineClass.apply(
                "fr-class-highlighted",
              );
            },
            refresh($btn) {
              if (
                (this as unknown as typeof editor).selection
                  .element()
                  .classList.contains("fr-class-highlighted")
              ) {
                $btn.addClass("fr-active");
              } else {
                $btn.removeClass("fr-active");
              }
            },
          });
        },
      ],
    };

    return this;
  };

  withFormatList = (
    listType: "OL" | "UL",
    options: Record<string, string>,
  ): ConfigBuilder => {
    this.config = {
      ...this.config,
      onInit: [
        ...this.config.onInit,
        (editor) => {
          FroalaEditor.RegisterCommand(`format${listType}`, {
            hasOptions() {
              return (this as unknown as typeof editor).opts.listAdvancedTypes;
            },
            options,
            refresh(e) {
              (this as unknown as typeof editor).lists.refresh(e, listType);
            },
            callback(_, t) {
              (
                (this as unknown as typeof editor).lists.format as (
                  type: string,
                  object: unknown,
                ) => object
              )(listType, t);
            },
            plugin: "lists",
          });
        },
      ],
    };

    return this;
  };

  withTabSpaces = (tabSpaces: number): ConfigBuilder => {
    this.config = {
      ...this.config,
      tabSpaces,
    };

    return this;
  };

  withComments = (
    users: Record<number | string, string>,
    currentUserId: number | string,
    toolbarName = "Kommentér",
  ): ConfigBuilder => {
    this.withToolbar((prev) => [...prev, "annotate"]);
    this.withPlugins((prev) => [...prev, "lance"]);
    this.withAllowedTags(["annotations"]);
    this.withAllowedAttr([
      "data-annotation-seq",
      "data-ant",
      "data-annotation-id",
      "data-selected",
      "data-track-changes-ignore",
    ]);

    this.config = {
      ...this.config,
      onInit: [
        ...this.config.onInit,
        () => {
          FroalaEditor.DefineIcon("annotate", {
            NAME: toolbarName,
            template: "text",
          });
        },
      ],
      lance: {
        users,
        currentUserId,
        assetPath: "/plugins/lance/",
        annotations: {
          permissions: {
            edit: "owner",
            delete: "any",
            resolve: "any",
          },
        },
        onInit: (id, lance, editor) => {
          const lanceApp = new window.LANCE.AnnotationsUI();
          const owner = lance.getAnnotations();

          (
            FroalaEditor.COMMANDS as { annotate: { title: string } }
          ).annotate.title = "Legg til kommentar";

          owner.addListener("annotation:created", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:deleted", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:resolved", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:selected", () => {
            editor.events.trigger("lance::open", [], false);
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:updated", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:commentcreated", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:commentdeleted", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:commentchanged", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:commentselected", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:reset", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:reload", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:enable", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:resize", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:done-editing", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:attribute-changed", () => {
            editor.events.trigger("contentChanged", [], false);
          });
          owner.addListener("annotation:renumbered", () => {
            editor.events.trigger("contentChanged", [], false);
          });

          owner.getLocalizedString = (key) => {
            switch (key) {
              case "on date":
                return "%dd.%MM.%Y - %hh:%mm";
              case "on full date":
                return "%dd.%MM.%Y - %hh:%mm";
              case "on time":
                return "%dd.%MM.%Y - %hh:%mm";
              case "reply to this comment":
                return "Svar på denne kommentaren";
              case "enter your comment":
                return "Skriv kommentar";
              case "now":
                return "Nå";
              case "resolve":
                return "Lukk";
              case "reopen":
                return "Åpne";
              case "edit":
                return "Endre";
              case "delete":
                return "Slett";
              case "insert comment":
                return "Legg til kommentar";
              case "enter comment":
                return "Skriv kommentar";
              case "delete this annotation?":
                return "Vil du slette denne markeringen?";
              case "delete this comment?":
                return "Vil du slette denne kommentaren?";
              case "save":
                return "Lagre";
              case "cancel":
                return "Avbryt";
              case "comment":
                return "Lagre";
              case "reply":
                return "Svar";
              case "delete-resolve":
                return "Slett/Lukk";
              case "1 minute ago":
                return "1 minutt siden";
              case "minutes ago":
                return "%ma minutter siden";
              case "on":
                return "den";
              case "months":
                return [
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "Mai",
                  "Jun",
                  "Jul",
                  "Aug",
                  "Sep",
                  "Okt",
                  "Nov",
                  "Des",
                ];
              case "full_months":
                return [
                  "Januar",
                  "Februar",
                  "Mars",
                  "April",
                  "Mai",
                  "Juni",
                  "Juli",
                  "August",
                  "September",
                  "Oktober",
                  "November",
                  "Desember",
                ];
              default: {
                return key;
              }
            }
          };
          lanceApp.init({
            owner,
            generateUI: true,
            generateCSS: false,
            blurAware: false,
            styleUrls: [],
            container:
              document.getElementById(`annotations-wrapper-${id}`) ?? null,
            toolbar: {
              buttons: [
                {
                  command: "comment-resolve",
                  display: "auto",
                },
                {
                  command: "comment-reopen",
                  display: "auto",
                },
                {
                  command: "comment-edit",
                  display: "auto",
                },
                {
                  command: "comment-delete",
                  display: "auto",
                },
              ],
              commands: [
                {
                  command: "comment-resolve",
                  title: "[[resolve]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjxzdmcgd2lkdGg9IjE2cHgiIGhlaWdodD0iMTZweCIgdmlld0JveD0iMCAwIDE2IDE2IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPg0KICAgIDxnIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPg0KICAgICAgICA8ZyBmaWxsPSIjMDAwMDAwIj4NCiAgICAgICAgICAgIDxwYXRoIGQ9Ik0wLDUgTDE2LDUgTDE2LDE2IEwwLDE2IEwwLDUgWiBNMiwxNCBMMTQsMTQgTDE0LDcgTDIsNyBMMiwxNCBaIE0xNCw1IEwxMiw1IEwxMiwyIEw3LDIgTDcsMCBMMTQsMCBMMTQsNSBaIE03LDkgTDksOSBMOSwxMiBMNywxMiBMNyw5IFoiPjwvcGF0aD4NCiAgICAgICAgPC9nPg0KICAgIDwvZz4NCjwvc3ZnPg==",
                  label: "",
                },
                {
                  command: "comment-reopen",
                  title: "[[reopen]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjxzdmcgd2lkdGg9IjE2cHgiIGhlaWdodD0iMTZweCIgdmlld0JveD0iMCAwIDE2IDE2IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPg0KICAgIDxnIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPg0KICAgICAgICA8ZyBmaWxsPSIjMDAwMDAwIj4NCiAgICAgICAgICAgIDxwYXRoIGQ9Ik0wLDUgTDE2LDUgTDE2LDE2IEwwLDE2IEwwLDUgWiBNNyw4IEw3LDEzIEw5LDEzIEw5LDggTDcsOCBaIE0xMyw1IEwxMywwIEwzLDAgTDMsNSBMNSw1IEw1LDIgTDExLDIgTDExLDUgTDEzLDUgWiI+PC9wYXRoPg0KICAgICAgICA8L2c+DQogICAgPC9nPg0KPC9zdmc+",
                  label: "",
                },
                {
                  command: "comment-edit",
                  title: "[[edit]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiIHZpZXdCb3g9IjAgMCAxNiAxNiIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj4NCiAgICA8ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4NCiAgICAgICAgPGcgZmlsbD0iIzAwMDAwMCI+DQogICAgICAgICAgICA8ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxLjAwMDAwMCwgMC4wMDAwMDApIj4NCiAgICAgICAgICAgICAgICA8Zz4NCiAgICAgICAgICAgICAgICAgICAgPHBhdGggZD0iTTIuNjk4MjYyODFlLTA4LDAgTC0xLjc5ODk4MDUyZS0wOSwxNiBMMTQsMTYgTDE0LDcgTDEyLDcgTDEyLDE0IEwyLDE0IEwyLDIgTDcuODU3MTQyNjgsMiBMNy44NTcxNDI2OCwwIEwyLjY5ODI2MjgxZS0wOCwwIEwyLjY5ODI2MjgxZS0wOCwwIFogTTUuNTkxNTk0MzIsOC4xMjgwMzY4OCBMNy4xMjgwMzY4OCw5LjQwODQwNTY4IEwxMy40MDg0MDU3LDEuODcxOTYzMTIgTDExLjg3MTk2MzEsMC41OTE1OTQzMjEgTDUuNTkxNTk0MzIsOC4xMjgwMzY4OCBMNS41OTE1OTQzMiw4LjEyODAzNjg4IFoiPjwvcGF0aD4NCiAgICAgICAgICAgICAgICA8L2c+DQogICAgICAgICAgICA8L2c+DQogICAgICAgIDwvZz4NCiAgICA8L2c+DQo8L3N2Zz4=",
                  label: "",
                },
                {
                  command: "comment-delete",
                  title: "[[delete]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiIHZpZXdCb3g9IjAgMCAxNiAxNiIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj4NCiAgICA8ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4NCiAgICAgICAgPGcgZmlsbD0iIzAwMDAwMCI+DQogICAgICAgICAgICA8Zz4NCiAgICAgICAgICAgICAgICA8cGF0aCBkPSJNMyw2IEwxMyw2IEwxMywxNiBMMywxNiBMMyw2IEwzLDYgWiBNNSw2IEwxMSw2IEwxMSwxNCBMNSwxNCBMNSw2IEw1LDYgWiBNNSwwIEwxMSwwIEwxMSwyIEw1LDIgTDUsMCBMNSwwIFogTTAsNCBMMTYsNCBMMTYsNiBMMCw2IEwwLDQgTDAsNCBaIj48L3BhdGg+DQogICAgICAgICAgICA8L2c+DQogICAgICAgIDwvZz4NCiAgICA8L2c+DQo8L3N2Zz4=",
                  label: "",
                },
                {
                  command: "comment-edit-ok",
                  title: "[[save]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiIHZpZXdCb3g9IjAgMCAxNiAxNiIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj4NCiAgICA8ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4NCiAgICAgICAgPGcgZmlsbD0iIzAwMDAwMCI+DQogICAgICAgICAgICA8Zz4NCiAgICAgICAgICAgICAgICA8cGF0aCBkPSJNOCwxNiBDMTIuNDE4Mjc4LDE2IDE2LDEyLjQxODI3OCAxNiw4IEMxNiwzLjU4MTcyMiAxMi40MTgyNzgsMS4xMzY4NjgzOGUtMTMgOCwxLjEzNjg2ODM4ZS0xMyBDMy41ODE3MjIsMS4xMzY4NjgzOGUtMTMgOC41MjY1MTI4M2UtMTQsMy41ODE3MjIgOC41MjY1MTI4M2UtMTQsOCBDOC41MjY1MTI4M2UtMTQsMTIuNDE4Mjc4IDMuNTgxNzIyLDE2IDgsMTYgTDgsMTYgTDgsMTYgTDgsMTYgWiBNOCwxNCBDNC42ODYyOTE1LDE0IDIsMTEuMzEzNzA4NSAyLDggQzIsNC42ODYyOTE1IDQuNjg2MjkxNSwyIDgsMiBDMTEuMzEzNzA4NSwyIDE0LDQuNjg2MjkxNSAxNCw4IEMxNCwxMS4zMTM3MDg1IDExLjMxMzcwODUsMTQgOCwxNCBMOCwxNCBaIE02Ljg3MDI3NzIxLDExLjc2NDI3MjEgTDExLjc3MTUzMTcsNi42NTY2NjcxIEwxMC4zMjg0NjgzLDUuMjcxOTA0MzMgTDYuODcwMjc3MjQsOC44ODA1NTQzIEw1LjY3MDE5NzU1LDcuNjM1NjcyNyBMNC4yMjk4MDI0NSw5LjAyMzIxMDc4IEw2Ljg3MDI3NzIxLDExLjc2NDI3MjEgWiI+PC9wYXRoPg0KICAgICAgICAgICAgPC9nPg0KICAgICAgICA8L2c+DQogICAgPC9nPg0KPC9zdmc+",
                  label: "",
                },
                {
                  command: "comment-reply-ok",
                  title: "[[comment]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiIHZpZXdCb3g9IjAgMCAxNiAxNiIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj4NCiAgICA8ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4NCiAgICAgICAgPGcgZmlsbD0iIzAwMDAwMCI+DQogICAgICAgICAgICA8Zz4NCiAgICAgICAgICAgICAgICA8cGF0aCBkPSJNOCwxNiBDMTIuNDE4Mjc4LDE2IDE2LDEyLjQxODI3OCAxNiw4IEMxNiwzLjU4MTcyMiAxMi40MTgyNzgsMS4xMzY4NjgzOGUtMTMgOCwxLjEzNjg2ODM4ZS0xMyBDMy41ODE3MjIsMS4xMzY4NjgzOGUtMTMgOC41MjY1MTI4M2UtMTQsMy41ODE3MjIgOC41MjY1MTI4M2UtMTQsOCBDOC41MjY1MTI4M2UtMTQsMTIuNDE4Mjc4IDMuNTgxNzIyLDE2IDgsMTYgTDgsMTYgTDgsMTYgTDgsMTYgWiBNOCwxNCBDNC42ODYyOTE1LDE0IDIsMTEuMzEzNzA4NSAyLDggQzIsNC42ODYyOTE1IDQuNjg2MjkxNSwyIDgsMiBDMTEuMzEzNzA4NSwyIDE0LDQuNjg2MjkxNSAxNCw4IEMxNCwxMS4zMTM3MDg1IDExLjMxMzcwODUsMTQgOCwxNCBMOCwxNCBaIE02Ljg3MDI3NzIxLDExLjc2NDI3MjEgTDExLjc3MTUzMTcsNi42NTY2NjcxIEwxMC4zMjg0NjgzLDUuMjcxOTA0MzMgTDYuODcwMjc3MjQsOC44ODA1NTQzIEw1LjY3MDE5NzU1LDcuNjM1NjcyNyBMNC4yMjk4MDI0NSw5LjAyMzIxMDc4IEw2Ljg3MDI3NzIxLDExLjc2NDI3MjEgWiI+PC9wYXRoPg0KICAgICAgICAgICAgPC9nPg0KICAgICAgICA8L2c+DQogICAgPC9nPg0KPC9zdmc+",
                  label: "",
                },
                {
                  command: "comment-reply-cancel",
                  title: "[[cancel]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiIHZpZXdCb3g9IjAgMCAxNiAxNiIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj4NCiAgICA8ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4NCiAgICAgICAgPGcgZmlsbD0iIzAwMDAwMCI+DQogICAgICAgICAgICA8Zz4NCiAgICAgICAgICAgICAgICA8cGF0aCBkPSJNOCwxNiBDMTIuNDE4Mjc4LDE2IDE2LDEyLjQxODI3OCAxNiw4IEMxNiwzLjU4MTcyMiAxMi40MTgyNzgsMCA4LDAgQzMuNTgxNzIyLDAgMCwzLjU4MTcyMiAwLDggQzAsMTIuNDE4Mjc4IDMuNTgxNzIyLDE2IDgsMTYgTDgsMTYgTDgsMTYgWiBNOCwxNCBDNC42ODYyOTE1LDE0IDIsMTEuMzEzNzA4NSAyLDggQzIsNC42ODYyOTE1IDQuNjg2MjkxNSwyIDgsMiBDMTEuMzEzNzA4NSwyIDE0LDQuNjg2MjkxNSAxNCw4IEMxNCwxMS4zMTM3MDg1IDExLjMxMzcwODUsMTQgOCwxNCBaIE04LDkuNDE0MjEzNTYgTDYuNzA3MTA2NzgsMTAuNzA3MTA2OCBMNiwxMS40MTQyMTM2IEw0LjU4NTc4NjQ0LDEwIEw1LjI5Mjg5MzIyLDkuMjkyODkzMjIgTDYuNTg1Nzg2NDQsOCBMNS4yOTI4OTMyMiw2LjcwNzEwNjc4IEw0LjU4NTc4NjQ0LDYgTDYsNC41ODU3ODY0NCBMNi43MDcxMDY3OCw1LjI5Mjg5MzIyIEw4LDYuNTg1Nzg2NDQgTDkuMjkyODkzMjIsNS4yOTI4OTMyMiBMMTAsNC41ODU3ODY0NCBMMTEuNDE0MjEzNiw2IEwxMC43MDcxMDY4LDYuNzA3MTA2NzggTDkuNDE0MjEzNTYsOCBMMTAuNzA3MTA2OCw5LjI5Mjg5MzIyIEwxMS40MTQyMTM2LDEwIEwxMCwxMS40MTQyMTM2IEw5LjI5Mjg5MzIyLDEwLjcwNzEwNjggTDgsOS40MTQyMTM1NiBMOCw5LjQxNDIxMzU2IFoiPjwvcGF0aD4NCiAgICAgICAgICAgIDwvZz4NCiAgICAgICAgPC9nPg0KICAgIDwvZz4NCjwvc3ZnPg==",
                  label: "",
                },
                {
                  command: "comment-edit-cancel",
                  title: "[[cancel]]",
                  iconUrl:
                    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiIHZpZXdCb3g9IjAgMCAxNiAxNiIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj4NCiAgICA8ZyBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4NCiAgICAgICAgPGcgZmlsbD0iIzAwMDAwMCI+DQogICAgICAgICAgICA8Zz4NCiAgICAgICAgICAgICAgICA8cGF0aCBkPSJNOCwxNiBDMTIuNDE4Mjc4LDE2IDE2LDEyLjQxODI3OCAxNiw4IEMxNiwzLjU4MTcyMiAxMi40MTgyNzgsMCA4LDAgQzMuNTgxNzIyLDAgMCwzLjU4MTcyMiAwLDggQzAsMTIuNDE4Mjc4IDMuNTgxNzIyLDE2IDgsMTYgTDgsMTYgTDgsMTYgWiBNOCwxNCBDNC42ODYyOTE1LDE0IDIsMTEuMzEzNzA4NSAyLDggQzIsNC42ODYyOTE1IDQuNjg2MjkxNSwyIDgsMiBDMTEuMzEzNzA4NSwyIDE0LDQuNjg2MjkxNSAxNCw4IEMxNCwxMS4zMTM3MDg1IDExLjMxMzcwODUsMTQgOCwxNCBaIE04LDkuNDE0MjEzNTYgTDYuNzA3MTA2NzgsMTAuNzA3MTA2OCBMNiwxMS40MTQyMTM2IEw0LjU4NTc4NjQ0LDEwIEw1LjI5Mjg5MzIyLDkuMjkyODkzMjIgTDYuNTg1Nzg2NDQsOCBMNS4yOTI4OTMyMiw2LjcwNzEwNjc4IEw0LjU4NTc4NjQ0LDYgTDYsNC41ODU3ODY0NCBMNi43MDcxMDY3OCw1LjI5Mjg5MzIyIEw4LDYuNTg1Nzg2NDQgTDkuMjkyODkzMjIsNS4yOTI4OTMyMiBMMTAsNC41ODU3ODY0NCBMMTEuNDE0MjEzNiw2IEwxMC43MDcxMDY4LDYuNzA3MTA2NzggTDkuNDE0MjEzNTYsOCBMMTAuNzA3MTA2OCw5LjI5Mjg5MzIyIEwxMS40MTQyMTM2LDEwIEwxMCwxMS40MTQyMTM2IEw5LjI5Mjg5MzIyLDEwLjcwNzEwNjggTDgsOS40MTQyMTM1NiBMOCw5LjQxNDIxMzU2IFoiPjwvcGF0aD4NCiAgICAgICAgICAgIDwvZz4NCiAgICAgICAgPC9nPg0KICAgIDwvZz4NCjwvc3ZnPg==",
                  label: "",
                },
              ],
            },
          });
        },
      },
    };

    return this;
  };

  withStickyToolbar = (offset = 56): ConfigBuilder => {
    this.config = {
      ...this.config,
      toolbarSticky: true,
      toolbarStickyOffset: offset,
    };

    return this;
  };

  withCustomConfig = (
    config: Partial<Config> | ((config: Config) => Config),
  ): ConfigBuilder => {
    if (typeof config === "function") {
      this.config = config(this.config);
    } else {
      this.config = {
        ...this.config,
        ...config,
      };
    }
    return this;
  };

  build(): Config {
    return this.config;
  }
}

export default ConfigBuilder;
